import { useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectStore } from '../stores/project';

/**
 * H3.5 (round-4) — "Start building" panel in Command Center.
 *
 * Renders when the current project has no active pipeline. Lets a
 * PM/PO describe their product intent in natural language and kicks
 * the 12-phase SDLC in one click:
 *
 *   1. POST /v1/packets with `{ charter_ref, title, intent }`.
 *   2. POST /v1/packets/{id}/run-full-pipeline.
 *   3. Navigate to /dashboard?pipeline_id=<chain_id>.
 *
 * Requires a selected project (H3.4 / ProjectSwitcher) and at least
 * one registered LLM provider (H1.4 / Settings). When either is
 * missing, the panel shows an explanatory card instead of the form.
 */
export function StartBuildingPanel(): JSX.Element {
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const activeProjectName = useProjectStore((s) =>
    s.projects.find((p) => p.id === s.activeProjectId)?.name,
  );
  const navigate = useNavigate();

  const [intent, setIntent] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!activeProjectId) {
    return (
      <div className="shakti-card p-6">
        <h2 className="text-lg font-semibold">Start a new build</h2>
        <p className="mt-2 text-sm text-fg-secondary">
          Select a project from the nav sidebar first.
        </p>
      </div>
    );
  }

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (intent.trim().length < 12) {
      setError('Describe your idea in a couple of sentences.');
      return;
    }
    setBusy(true);
    try {
      const derivedTitle =
        title.trim() || intent.trim().split(/\n|\./)[0]?.slice(0, 80) || 'New build';
      const derivedRef = `PKT-${Date.now().toString(36).toUpperCase()}`;

      const packet = await shaktiApi.createPacket({
        charter_ref: derivedRef,
        title: derivedTitle,
        intent,
      });

      const chain = await shaktiApi.runFullPipeline(packet.id);

      await navigate({
        to: '/dashboard',
        search: { pipeline_id: chain.chain_id },
      });
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  }

  return (
    <section
      className="shakti-card flex flex-col gap-4 p-6"
      aria-labelledby="start-building-title"
      data-testid="start-building-panel"
    >
      <header>
        <h2 id="start-building-title" className="text-lg font-semibold">
          Start a new build
        </h2>
        <p className="text-sm text-fg-secondary">
          Describe what you want to build in {activeProjectName ?? 'this project'}. Shakti will run the
          12-phase SDLC against your connected LLM provider.
        </p>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-xs">
          Title <span className="text-fg-muted">(optional — we'll infer one)</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task manager MVP"
            disabled={busy}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="intent-title"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Product intent
          <textarea
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            rows={5}
            placeholder="A lightweight task manager for two-person teams. Stripe billing (monthly + annual). Email notifications on assignments. Dark-mode UI by default."
            required
            disabled={busy}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="intent-textarea"
          />
        </label>
        {error ? (
          <div
            role="alert"
            className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
          >
            {error}
          </div>
        ) : null}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy || intent.trim().length < 12}
            className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
            data-testid="start-building-submit"
          >
            {busy ? 'Starting pipeline…' : 'Start Building'}
          </button>
        </div>
      </form>
    </section>
  );
}

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.body?.code === 'pipeline.provider_missing' || e.body?.error?.includes('provider')) {
      return `Connect an LLM provider in Settings first. (${e.status})`;
    }
    return `${e.body?.error ?? e.message} (${e.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}
